import React, { useEffect, useMemo, useReducer, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { fetchProfileGroups } from 'api/auth'
import { arrayReducer, initStateArray } from 'reducers/default'
import PortalModal from 'components/Shared/PortalModal'
import Searchbar from 'components/Shared/Searchbar'
import LoadingSpinner, { SpinnerSize } from 'components/Shared/LoadingSpinner'
import { getAccountInitials } from './accountAvatar'
import { CloseXIcon, CheckIcon } from 'images'

const AccountSwitcherModal = ({ isOpen, currentPartnerId, switchingAccountId, onClose, onSelect }) => {
  const isSwitching = Boolean(switchingAccountId)
  // Block dismissal while a switch request is in flight, so an error toast
  // never fires after the modal that explains it has already gone away.
  const handleClose = isSwitching ? () => {} : onClose
  const { t } = useTranslation()
  const email = useSelector((state) => state.user.email)
  const cognitoGroups = useSelector((state) => state.user.cognitoGroups) ?? []

  const defaultGroups = useMemo(() => cognitoGroups.map((group) => ({ id: group })), [cognitoGroups])
  const [groups, dispatchGroups] = useReducer(arrayReducer, initStateArray)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!isOpen) return
    fetchProfileGroups(dispatchGroups, defaultGroups)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const filteredAccounts = useMemo(() => {
    if (!search) return groups.items
    const query = search.toLowerCase()
    return groups.items.filter((group) => group.id?.toLowerCase().includes(query))
  }, [groups.items, search])

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={handleClose}
      className="account-switcher-modal"
      >
      <>
      <div className="account-switcher-modal__header">
        <div>
          <p className="account-switcher-modal__title">{t('AccountSwitcher.title')}</p>

          {email && <span className="account-switcher-modal__email">{email}</span>}
        </div>

        <button
          type="button"
          className="account-switcher-modal__close"
          onClick={handleClose}
          disabled={isSwitching}
        >
          <CloseXIcon width={12} height={12} />
        </button>
      </div>

      <div className="account-switcher-modal__body">
        <div className="account-switcher-modal__search-container">
          <Searchbar
            handleChange={setSearch}
            defaultValue=""
            placeholder={t('AccountSwitcher.searchPlaceholder')}
            ariaLabel="account-search"
            autoFocus
          />
        </div>

        <div className="account-switcher-modal__list">
          {groups.loading && <LoadingSpinner className="p-3" />}

          {groups.error && (
            <p className="account-switcher-modal__error">{t('AccountSwitcher.loadError')}</p>
          )}

          {!groups.loading &&
            filteredAccounts.map((account) => {
              const isSwitchingToThis = account.id === switchingAccountId

              return (
                <button
                  type="button"
                  key={account.id}
                  className={`account-switcher-modal__item ${
                    account.id === currentPartnerId ? 'account-switcher-modal__item--active' : ''
                  }`}
                  onClick={() => onSelect(account.id)}
                  disabled={isSwitching}
                >
                  <span className="account-switcher-modal__avatar" aria-hidden="true">
                    {getAccountInitials(account.id)}
                  </span>

                  <span className="account-switcher-modal__label">{account.id}</span>

                  {isSwitchingToThis && (
                    <span className="account-switcher-modal__check">
                      <LoadingSpinner size={SpinnerSize.SMALL} showText={false} portalBackground={false} />
                    </span>
                  )}

                  {!isSwitchingToThis && account.id === currentPartnerId && (
                    <span className="account-switcher-modal__check">
                      <CheckIcon width={16} height={16} />
                    </span>
                  )}
                </button>
              )
            })}
        </div>

        <div className="account-switcher-modal__footer">
          {t('AccountSwitcher.accountsCount', { count: groups.items.length })}
        </div>
      </div>
      </>
    </PortalModal>
  )
}

AccountSwitcherModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  currentPartnerId: PropTypes.string,
  switchingAccountId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
}

export default AccountSwitcherModal
